import React, { useState } from "react";
import axios from "axios";
import { ErrorBanner, Spinner, inputStyle, secondaryBtn, primaryBtn } from "./MessagesCommon";
import { messages } from "../config/messages";
import { PROXY_BROADCAST_MESSAGE, PROXY_BROADCAST_POLL_MESSAGE } from "../config/settings";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { Box } from "@mui/material";

export default function ComposeModal({ onClose, onSent, selectedConnectors = [] }) {
  const [form, setForm] = useState({
    subject: "",
    body: "",
    messageType: "GENERIC",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const getTargetName = (target) =>
    target?.participantName || target?.connectorName || target?.recipient || target?.name || target?.id || "Unknown recipient";

  const getTargetError = (target) =>
    target?.error || target?.message || target?.errorMessage || "Delivery failed";

  const handleSend = async () => {
    setError(null);
    setResult({ tone: "neutral", message: "Sending…" });
    setLoading(true);

    try {
      const payload = {
        body: form.body,
        subject: form.subject || undefined,
        messageType: form.messageType || "GENERIC",
        recipientConnectors: selectedConnectors,
      };

      const { data: initialData } = await axios.post(PROXY_BROADCAST_MESSAGE, payload);
      let data = initialData;
      const terminalStatuses = ["DELIVERED", "PARTIAL", "FAILED"];
      const messageId = data?.id ?? data?.messageId;

      if (!terminalStatuses.includes(data?.status)) {
        if (!messageId) {
          throw new Error(data?.message || "The broadcast was accepted, but no message ID was returned.");
        }

        const deadline = Date.now() + 60_000;

        while (data?.status === "PENDING" && Date.now() < deadline) {
          await wait(2000);
          if (Date.now() >= deadline) break;
          const pollUrl = PROXY_BROADCAST_POLL_MESSAGE.replace("{id}", encodeURIComponent(messageId));
          const response = await axios.get(pollUrl);
          data = response.data;
        }

        if (data?.status === "PENDING") {
          setResult({ tone: "info", message: "Accepted, still delivering, see Broadcast History." });
          return;
        }
      }

      const targets = Array.isArray(data?.targets)
        ? data.targets
        : Array.isArray(data?.recipients)
          ? data.recipients
          : [];
      const delivered = data?.delivered ?? targets.filter((target) => target?.delivered === true).length;
      const targetCount = data?.targetCount ?? (targets.length || selectedConnectors.length);

      if (data?.status === "DELIVERED") {
        setResult({ tone: "success", message: `Delivered to ${delivered}/${targetCount}.` });
        setTimeout(() => onSent?.(), 2000);
      } else if (data?.status === "PARTIAL") {
        const failedTargets = targets.filter((target) => target?.delivered === false);
        const details = failedTargets.length
          ? failedTargets.map((target) => `${getTargetName(target)}: ${getTargetError(target)}`).join("; ")
          : (data?.message || "Some recipients were not delivered.");
        setResult({
          tone: "warning",
          message: `Partially delivered (${delivered}/${targetCount}). Undelivered targets will be retried automatically. ${details}`,
        });
      } else if (data?.status === "FAILED") {
        const targetErrors = targets
          .filter((target) => target?.error || target?.message || target?.errorMessage || target?.delivered === false)
          .map((target) => `${getTargetName(target)}: ${getTargetError(target)}`);
        setResult({
          tone: "error",
          message: `${data?.message || "Broadcast delivery failed."}${targetErrors.length ? ` ${targetErrors.join("; ")}` : ""} Failed recipients will be retried automatically.`,
        });
      } else {
        setResult({ tone: "error", message: data?.message || `Unexpected broadcast status: ${data?.status || "unknown"}.` });
      }
    } catch (e) {
      setResult(null);
      setError(e.response?.data?.message || e.message || "Unable to send broadcast.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.35)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "24px", paddingTop: "150px", overflowY: "auto",
    }}
    onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "#fff", borderRadius: "16px", width: "100%", maxWidth: "560px",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ padding: "20px 28px", borderBottom: "1px solid #f0f0f4", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#1a202c" }}>
            {messages.messagesBroadcast_promt_partA} ({selectedConnectors.length})  {messages.messagesBroadcast_promt_partB}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#a0aec0" }}>✕</button>
        </div>

        <div style={{ padding: "24px 28px" }}>
          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

          <div style={{ marginBottom: "16px", fontSize: "13px", color: "#718096", background: "#f7fafc", padding: "8px 12px", borderRadius: "6px" }}>
            <strong>{messages.messagesBroadcast_recipients}</strong> {selectedConnectors.join(", ")}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4a5568", marginBottom: "6px" }}>Subject</label>
            <input value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Optional subject" style={inputStyle} />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4a5568", marginBottom: "6px" }}>Message Type</label>
            <select value={form.messageType} onChange={(e) => set("messageType", e.target.value)} style={inputStyle}>
              <option value="GENERIC">GENERIC</option>
              <option value="ALERT">ALERT</option>
              <option value="INVOICE_PAID">INVOICE_PAID</option>
              <option value="CONTRACT_NEGOTIATION">CONTRACT_NEGOTIATION</option>
              <option value="MAINTENANCE">MAINTENANCE</option>
            </select>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#4a5568", marginBottom: "6px" }}>Body *</label>
            <textarea value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Message body…" rows={5} style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          {result && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: "12px 16px",
                borderRadius: 2,
                bgcolor: ({ success: "#f0fff4", warning: "#fffaf0", error: "#fff5f5", neutral: "#f7fafc", info: "#ebf8ff" }[result.tone] || "#f7fafc"),
                border: `1px solid ${{ success: "#9ae6b4", warning: "#fbd38d", error: "#fc8181", neutral: "#e2e8f0", info: "#90cdf4" }[result.tone] || "#e2e8f0"}`,
                color: ({ success: "#22543d", warning: "#744210", error: "#822727", neutral: "#4a5568", info: "#2a4365" }[result.tone] || "#4a5568"),
                fontSize: "14px",
                fontWeight: 600,
                mt: 2,
              }}
            >
              {result.tone === "success" ? (
                <CheckCircleOutlineOutlinedIcon fontSize="small" />
              ) : result.tone === "error" || result.tone === "warning" ? (
                <ErrorOutlineOutlinedIcon fontSize="small" />
              ) : result.tone === "neutral" ? (
                <Spinner size={14} />
              ) : null}
              {result.message}
            </Box>
          )}

          {!result && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
              <button onClick={onClose} style={secondaryBtn}>Cancel</button>
              <button
                onClick={handleSend}
                disabled={loading || !form.body.trim()}
                style={{ ...primaryBtn, opacity: (loading || !form.body.trim()) ? 0.5 : 1 }}
              >
                {loading ? <><Spinner size={14} /> Sending…</> : "Send Broadcast"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
